import {
  defaultRangeExtractor,
  useVirtualizer,
  type Range as VirtualRange,
} from '@tanstack/react-virtual'
import { ChevronsDown, Search } from 'lucide-react'
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { LogLine } from '@/components/shared/logs-explorer/components/log-line'
import { LogLinesSkeleton } from '@/components/shared/logs-explorer/components/logs-skeleton'
import * as Styled from '@/components/shared/logs-explorer/components/styles'
import { useLogSelection } from '@/components/shared/logs-explorer/hooks/use-log-selection'
import { useLogsExplorer } from '@/components/shared/logs-explorer/hooks/use-logs-explorer'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { Log } from '@/graphql.types'

type LogsViewerProps = {
  className?: string
}

const OVERSCAN = 10

type VirtualLogRowProps = {
  log: Log
  logIndex: number
  logsLength: number
  filterText?: string
  isExpanded: boolean
  onToggleExpand: (logId: string) => void
}

const VirtualLogRow = memo(function VirtualLogRow({
  log,
  logIndex,
  logsLength,
  filterText,
  isExpanded,
  onToggleExpand,
}: VirtualLogRowProps) {
  const handleToggleExpand = useCallback(() => {
    onToggleExpand(log.id)
  }, [log.id, onToggleExpand])

  return (
    <LogLine
      log={log}
      filterText={filterText}
      isHighlighted={logIndex % 2 === 0}
      isFirstLine={logIndex === 0}
      isLastLine={logIndex === logsLength - 1}
      isExpanded={isExpanded}
      onToggleExpand={handleToggleExpand}
    />
  )
})

export const LogsViewer = ({ className }: LogsViewerProps) => {
  const {
    logs,
    isLoading,
    resolvedFilter,
    registerScrollToBottom,
    fetchLogsBefore,
    prependLogs,
  } = useLogsExplorer()

  const parentRef = useRef<HTMLDivElement>(null)
  const [showButton, setShowButton] = useState(false)
  const [isLoadMoreLoading, setIsLoadMoreLoading] = useState(false)
  const [noMoreLogs, setNoMoreLogs] = useState(false)
  const [expandedLogIds, setExpandedLogIds] = useState<Set<string>>(new Set())
  const shouldScrollToBottomRef = useRef(true)
  const isUserScrollingRef = useRef(false)
  const isLoadingMoreRef = useRef(false)
  const prevLogsLengthRef = useRef(0)

  const hasMoreLogsBefore = useMemo(() => {
    if (noMoreLogs || !logs.length || !resolvedFilter.from) {
      return false
    }
    return logs.length >= 1000
  }, [noMoreLogs, logs.length, resolvedFilter.from])

  const toggleLogExpanded = useCallback((logId: string) => {
    setExpandedLogIds((prev) => {
      const next = new Set(prev)
      if (next.has(logId)) {
        next.delete(logId)
      } else {
        next.add(logId)
      }
      return next
    })
  }, [])

  const getLogIdByIndex = useCallback(
    (index: number) => {
      const logIndex = hasMoreLogsBefore ? index - 1 : index
      return logs[logIndex]?.id
    },
    [hasMoreLogsBefore, logs]
  )

  const estimateSize = useCallback(
    (index: number) => {
      if (hasMoreLogsBefore && index === 0) {
        return 40
      }
      const logId = getLogIdByIndex(index)
      return logId && expandedLogIds.has(logId) ? 200 : 26
    },
    [expandedLogIds, hasMoreLogsBefore, getLogIdByIndex]
  )

  const { pinnedIndexes } = useLogSelection({
    containerRef: parentRef,
    logs,
    rowIndexOffset: hasMoreLogsBefore ? 1 : 0,
  })

  const rangeExtractor = useCallback(
    (range: VirtualRange) => {
      const indexes = defaultRangeExtractor(range)
      const extraIndexes = pinnedIndexes.filter(
        (index) => index < range.count && !indexes.includes(index)
      )
      if (extraIndexes.length === 0) {
        return indexes
      }
      return [...indexes, ...extraIndexes].sort((a, b) => a - b)
    },
    [pinnedIndexes]
  )

  const virtualizer = useVirtualizer({
    count: logs.length + (hasMoreLogsBefore ? 1 : 0),
    getScrollElement: () => parentRef.current,
    estimateSize,
    overscan: OVERSCAN,
    rangeExtractor,
  })

  const onLoadMore = async () => {
    shouldScrollToBottomRef.current = false
    isLoadingMoreRef.current = true
    setIsLoadMoreLoading(true)

    const newLogs = await fetchLogsBefore(
      new Date(logs[0].unixTimestamp / 1000)
    )

    if (newLogs.length === 0) {
      setNoMoreLogs(true)
      setIsLoadMoreLoading(false)
      isLoadingMoreRef.current = false
      return
    }

    const newLogsCount = newLogs.length
    prependLogs(newLogs)
    setIsLoadMoreLoading(false)

    requestAnimationFrame(() => {
      virtualizer.scrollToIndex(newLogsCount + 1, { align: 'start' })
      isLoadingMoreRef.current = false
    })
  }

  // Reset expanded items on filter change
  useEffect(() => {
    setExpandedLogIds(new Set())
    setNoMoreLogs(false)
    shouldScrollToBottomRef.current = true
    isUserScrollingRef.current = false
    prevLogsLengthRef.current = 0
  }, [resolvedFilter])

  const scrollToBottom = useCallback(
    (smooth?: boolean) => {
      if (logs.length > 0) {
        const totalItems = logs.length + (hasMoreLogsBefore ? 1 : 0)
        virtualizer.scrollToIndex(totalItems - 1, {
          align: 'end',
          behavior: smooth === true ? 'smooth' : 'auto',
        })
      }
    },
    [logs.length, virtualizer, hasMoreLogsBefore]
  )

  const handleScrollToBottomClick = useCallback(() => {
    scrollToBottom()
  }, [scrollToBottom])

  // Register scroll-to-bottom with provider
  useEffect(() => {
    registerScrollToBottom(scrollToBottom)
  }, [registerScrollToBottom, scrollToBottom])

  const checkIsAtBottom = useCallback(() => {
    const { range } = virtualizer
    if (!range) {
      return true
    }
    const totalItems = logs.length + (hasMoreLogsBefore ? 1 : 0)
    return range.endIndex + OVERSCAN >= totalItems - 1
  }, [virtualizer, logs.length, hasMoreLogsBefore])

  useEffect(() => {
    const scrollElement = parentRef.current
    if (!scrollElement) {
      return
    }

    const handleScroll = () => {
      const isAtBottom = checkIsAtBottom()
      setShowButton(!isAtBottom)
      shouldScrollToBottomRef.current = isAtBottom

      if (!isAtBottom) {
        isUserScrollingRef.current = true
      }
    }

    handleScroll()

    scrollElement.addEventListener('scroll', handleScroll)
    return () => scrollElement.removeEventListener('scroll', handleScroll)
  }, [checkIsAtBottom])

  useLayoutEffect(() => {
    if (isLoadingMoreRef.current) {
      return
    }

    const isInitialLoad = prevLogsLengthRef.current === 0
    prevLogsLengthRef.current = logs.length

    if (shouldScrollToBottomRef.current && logs.length > 0) {
      scrollToBottom(!isInitialLoad)
    }
  }, [logs.length, scrollToBottom])

  useEffect(() => {
    const scrollElement = parentRef.current
    if (!scrollElement || !logs.length) {
      return
    }
    const { scrollTop, scrollHeight, clientHeight } = scrollElement
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 50
    setShowButton(!isAtBottom)
  }, [logs.length])

  if (isLoading || !logs?.length) {
    return (
      <Styled.LogViewerWrapper
        hasContentAbove={false}
        className={cn('flex-1 min-h-0', className)}
      >
        {isLoading ? (
          <LogLinesSkeleton />
        ) : (
          <Styled.LogRowContent className="hover:bg-background h-full">
            <Styled.ConsoleContent className="w-full col-span-2 flex items-start text-xs py-10 hover:bg-background">
              <div className="flex flex-col gap-4 items-center w-full">
                <Search />
                <div>No logs found</div>
                <div>
                  Logs can take up to 2 minutes to appear in the dashboard
                </div>
              </div>
            </Styled.ConsoleContent>
          </Styled.LogRowContent>
        )}
      </Styled.LogViewerWrapper>
    )
  }

  return (
    <Styled.LogViewerWrapper
      hasContentAbove={false}
      className={cn('flex-1 flex flex-col min-h-0', className)}
    >
      <div
        ref={parentRef}
        className={cn(
          'font-mono overflow-x-hidden overflow-y-auto',
          'bg-no-repeat bg-background',
          'flex-1 min-h-0'
        )}
        style={{
          overscrollBehavior: 'contain',
        }}
      >
        <div
          style={{
            height: `${virtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          {virtualizer.getVirtualItems().map((virtualRow) => {
            const isLoadMoreRow = hasMoreLogsBefore && virtualRow.index === 0
            const logIndex = hasMoreLogsBefore
              ? virtualRow.index - 1
              : virtualRow.index
            const log = isLoadMoreRow ? null : logs[logIndex]
            const isExpanded =
              !isLoadMoreRow && log && expandedLogIds.has(log.id)

            return (
              <div
                key={virtualRow.key}
                data-index={virtualRow.index}
                ref={virtualizer.measureElement}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                {isLoadMoreRow ? (
                  <div className="w-full flex items-center py-2 bg-background">
                    <Button
                      onClick={onLoadMore}
                      variant="outline"
                      className="mx-auto"
                    >
                      {isLoadMoreLoading ? 'Loading...' : 'Load more'}
                    </Button>
                  </div>
                ) : (
                  <VirtualLogRow
                    key={log?.id}
                    log={log!}
                    logIndex={logIndex}
                    logsLength={logs.length}
                    filterText={resolvedFilter?.filterText}
                    isExpanded={!!isExpanded}
                    onToggleExpand={toggleLogExpanded}
                  />
                )}
              </div>
            )
          })}
        </div>
      </div>
      <Styled.ToBottomButton
        isVisible={showButton}
        variant="default"
        aria-label="Scroll to bottom"
        onClick={handleScrollToBottomClick}
      >
        <ChevronsDown />
      </Styled.ToBottomButton>
    </Styled.LogViewerWrapper>
  )
}
